import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { ExcerptImagePreview } from "@/components/image-studio/excerpt-image/ExcerptImagePreview";
import { MIN_SCALE, MAX_SCALE } from "@/components/image-studio/excerpt-image/usePinchPan";
import type { OverlayConfig } from "@/components/image-studio/excerpt-image/OverlayPanel";
import type { RefObject } from "react";

type PinchPan = {
  handlers: Record<string, unknown>;
  transformStyle: React.CSSProperties;
  scale: number;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => boolean;
};

type Props = {
  previewRef: RefObject<HTMLDivElement>;
  previewColumnRef: RefObject<HTMLDivElement>;
  previewBoxRef: RefObject<HTMLDivElement>;
  zoomSurfaceRef: RefObject<HTMLDivElement>;
  frameWidth: number;
  frameHeight: number;
  previewWidth: number;
  previewHeight: number;
  isDesktop: boolean;
  isOverflowing: boolean;
  showHint: boolean;
  pinchPan: PinchPan;
  backgroundCss: string;
  textColor: string;
  fontStack: string;
  dividerColor: string;
  bgMode: "light" | "dark" | "custom" | "brand" | "gradient";
  overlay: OverlayConfig;
  bodyHtml: string;
  authorName: string;
  pubTitle: string;
  displayUrl: string;
  logoRenderUrl: string | null;
  isDefaultCornerMark: boolean;
  decoOpacity: number;
  logoOpacity: number;
};

export function ExcerptImagePreviewPane(p: Props) {
  return (
    <div ref={p.previewColumnRef} className="order-1 flex max-h-[42vh] min-w-0 shrink-0 flex-col items-center gap-2 overflow-y-auto sm:max-h-[48vh] lg:order-2 lg:max-h-none lg:overflow-visible lg:self-start lg:sticky lg:top-4 lg:pr-1">
      <p data-preview-label className="hidden self-center text-sm text-muted-foreground lg:block">Preview · 1200 × auto</p>
      <div
        className="flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/30 p-2 shadow-elevated sm:p-3"
        style={{ width: p.frameWidth, height: p.frameHeight }}
      >
        <div ref={p.previewBoxRef} className="relative flex h-full w-full items-center justify-center">
          <div
            ref={p.zoomSurfaceRef}
            className="relative"
            style={{
              width: p.previewWidth,
              height: p.previewHeight,
              overflow: "hidden",
              touchAction: p.isDesktop ? "auto" : "none",
            }}
            {...(p.isDesktop ? {} : p.pinchPan.handlers)}
          >
            <div style={p.isDesktop ? undefined : p.pinchPan.transformStyle}>
              <div style={{ transform: `scale(${p.previewWidth / 1200})`, transformOrigin: "top left", width: 1200 }}>
                <ExcerptImagePreview
                  previewRef={p.previewRef}
                  backgroundCss={p.backgroundCss}
                  textColor={p.textColor}
                  fontStack={p.fontStack}
                  dividerColor={p.dividerColor}
                  bgMode={p.bgMode}
                  overlay={p.overlay}
                  bodyHtml={p.bodyHtml}
                  authorName={p.authorName}
                  pubTitle={p.pubTitle}
                  displayUrl={p.displayUrl}
                  logoRenderUrl={p.logoRenderUrl}
                  isDefaultCornerMark={p.isDefaultCornerMark}
                  decoOpacity={p.decoOpacity}
                  logoOpacity={p.logoOpacity}
                />
              </div>
            </div>
          </div>
          {!p.isDesktop && (
            <>
              {p.showHint && (
                <div className="pointer-events-none absolute left-3 top-3 z-20 rounded-md bg-background/85 px-2 py-1 text-xs font-medium text-foreground shadow-sm backdrop-blur transition-opacity">
                  {Math.round(p.pinchPan.scale * 100)}%
                </div>
              )}
              <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1 rounded-md bg-background/85 p-1 shadow-sm backdrop-blur lg:hidden">
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={p.pinchPan.zoomOut} aria-label="Zoom out" disabled={p.pinchPan.scale <= MIN_SCALE + 0.001}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={p.pinchPan.zoomIn} aria-label="Zoom in" disabled={p.pinchPan.scale >= MAX_SCALE - 0.001}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={p.pinchPan.reset} aria-label="Reset zoom" disabled={p.pinchPan.scale === 1}>
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      {p.isOverflowing && (
        <div style={{ width: p.frameWidth }} className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          Excerpt is too long to fit in a 1200 × 1800 image. Download is disabled until the excerpt fits — trim the text and the Download button will re-enable.
        </div>
      )}
    </div>
  );
}
